import React from "react";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell,
} from "recharts";
import { formatINRCompact } from "@/lib/format";

const PALETTE = ["#0f172a", "#0ea5e9", "#8b5cf6", "#f59e0b", "#10b981", "#ef4444", "#64748b", "#ec4899"];

function HeatCell({ count, winRate }) {
  // color intensity by winRate (0..100); muted if no matches
  const wr = winRate == null ? null : Number(winRate);
  const bg = wr == null ? "#f8fafc" : `rgba(16,185,129,${0.12 + (wr / 100) * 0.7})`;
  return (
    <td className="px-3 py-2 text-center border border-slate-100" style={{ backgroundColor: bg }}>
      {count === 0 || count == null ? (
        <span className="text-xs text-slate-300">—</span>
      ) : (
        <div>
          <div className="font-mono-num text-sm font-semibold text-slate-900">{wr != null ? `${wr.toFixed(0)}%` : "—"}</div>
          <div className="text-[10px] text-slate-500">{count} match{count === 1 ? "" : "es"}</div>
        </div>
      )}
    </td>
  );
}

export default function ReportChart({ chart }) {
  if (!chart) return null;
  const { type } = chart;

  if (type === "funnel") {
    const data = chart.data || [];
    const max = Math.max(1, ...data.map((d) => d.value || 0));
    return (
      <div className="bg-white border border-slate-200 rounded-md p-5 space-y-2" data-testid="report-chart-funnel">
        {data.map((d, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-44 text-sm text-slate-700 shrink-0 text-right">{d.label}</div>
            <div className="flex-1 bg-slate-50 rounded h-8 relative overflow-hidden">
              <div className="h-8 rounded flex items-center px-2 text-white text-xs font-semibold"
                style={{ width: `${Math.max((d.value / max) * 100, 6)}%`, backgroundColor: PALETTE[i % PALETTE.length] }}>
                {d.value}
              </div>
            </div>
            {d.pct != null && <div className="w-16 text-xs text-slate-500 font-mono-num text-right">{d.pct.toFixed(0)}%</div>}
          </div>
        ))}
      </div>
    );
  }

  if (type === "heatmap") {
    const { rows = [], cols = [], cells = {} } = chart;
    return (
      <div className="bg-white border border-slate-200 rounded-md p-4 overflow-x-auto scrollbar-thin" data-testid="report-chart-heatmap">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="px-3 py-2 text-left label-eyebrow !text-slate-500">{chart.row_label || ""}</th>
              {cols.map((c) => (
                <th key={c} className="px-3 py-2 text-center label-eyebrow !text-slate-500">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r}>
                <td className="px-3 py-2 text-sm font-medium text-slate-800 border border-slate-100">{r}</td>
                {cols.map((c) => {
                  const cell = (cells[r] || {})[c] || { count: 0, winRate: null };
                  return <HeatCell key={c} count={cell.count} winRate={cell.winRate} />;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // bar / stackedBar / winBar / line
  const data = chart.data || [];
  const series = chart.series || [];
  const xKey = chart.xKey || "label";
  const isCurrency = chart.value_format === "currency";
  const tickFmt = isCurrency ? (v) => formatINRCompact(v) : (v) => v;

  const ChartEl = type === "line" ? LineChart : BarChart;

  return (
    <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="report-chart">
      <ResponsiveContainer width="100%" height={340}>
        <ChartEl data={data} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: "#64748b" }} interval={0} angle={data.length > 6 ? -20 : 0} textAnchor={data.length > 6 ? "end" : "middle"} height={data.length > 6 ? 60 : 30} />
          <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={tickFmt} />
          <Tooltip formatter={(v) => (isCurrency ? formatINRCompact(v) : v)} />
          {series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
          {series.map((s, i) =>
            type === "line" ? (
              <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={s.color || PALETTE[i % PALETTE.length]} strokeWidth={2} dot={{ r: 3 }} />
            ) : (
              <Bar key={s.key} dataKey={s.key} name={s.label} stackId={type === "stackedBar" ? "a" : undefined} fill={s.color || PALETTE[i % PALETTE.length]} radius={type === "stackedBar" ? 0 : [4, 4, 0, 0]}>
                {type === "winBar" && data.map((_, di) => <Cell key={di} fill={PALETTE[di % PALETTE.length]} />)}
              </Bar>
            )
          )}
        </ChartEl>
      </ResponsiveContainer>
    </div>
  );
}
